import React, { useState, useMemo, useEffect } from 'react';
import {
  Building2,
  Plus,
  Search,
  Filter,
  DollarSign,
  Calendar,
  Phone,
  Mail,
  MapPin,
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Edit3,
  Trash2,
  Printer,
  Download,
  CreditCard,
  Banknote,
  Landmark,
  Layers,
  ChevronRight,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Percent,
  Check,
  X,
  Receipt,
  ExternalLink,
  ShieldCheck,
  Hash,
  Wallet,
} from 'lucide-react';
import {
  AppUser,
  ContractedCompany,
  ContractPaymentRecord,
  PaymentMethod,
  ServiceTicket,
} from '../types';
import {
  calculateTicketTotal,
  calculateTicketPartsCost,
  formatTL,
  formatDateTR,
  formatDateShortTR,
  getStoredContractedCompanies,
  saveContractedCompanies,
  generateNextContractCompanyId,
  calculateContractCompanyMetrics,
  STATUS_COLORS,
  STATUS_LABELS,
  getStatusColors,
  SERVICE_TYPE_COLORS,
  SERVICE_TYPE_LABELS,
} from '../utils/storage';

interface ContractedCompaniesViewProps {
  tickets: ServiceTicket[];
  onSelectTicket?: (ticket: ServiceTicket) => void;
  onOpenNewTicketForCompany?: (company: ContractedCompany) => void;
  onUpdateTickets?: (tickets: ServiceTicket[]) => void;
  isAdmin?: boolean;
  currentUser?: AppUser | null;
}

export const ContractedCompaniesView: React.FC<ContractedCompaniesViewProps> = ({
  tickets,
  onSelectTicket,
  onOpenNewTicketForCompany,
  isAdmin = false,
  currentUser,
}) => {
  const reportableTickets = useMemo(() => tickets.filter(t => !t.isDeleted), [tickets]);

  const [companies, setCompanies] = useState<ContractedCompany[]>(() =>
    getStoredContractedCompanies()
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'passive'>('all');
  
  // Period filter (Month selector)
  const currentMonthKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [selectedPeriod, setSelectedPeriod] = useState<string>(currentMonthKey); // 'all' or 'YYYY-MM'

  // Modal States
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<ContractedCompany | null>(null);
  
  // Company Form State
  const [formName, setFormName] = useState('');
  const [formCode, setFormCode] = useState('');
  const [formAuthPerson, setFormAuthPerson] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formTaxOffice, setFormTaxOffice] = useState('');
  const [formTaxNumber, setFormTaxNumber] = useState('');
  const [formMonthlyFee, setFormMonthlyFee] = useState<number>(50000);
  const [formBillingCycle, setFormBillingCycle] = useState<'Aylık' | '3 Aylık' | '6 Aylık' | 'Yıllık'>('Aylık');
  const [formStartDate, setFormStartDate] = useState(new Date().toISOString().substring(0, 10));
  const [formNotes, setFormNotes] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);

  // Payment Collection Modal State
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentCompany, setPaymentCompany] = useState<ContractedCompany | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('EFT/Havale');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().substring(0, 10));
  const [paymentPeriodMonth, setPaymentPeriodMonth] = useState(currentMonthKey);
  const [paymentNotes, setPaymentNotes] = useState('');

  // Toast message
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Sync and save companies
  const handleUpdateCompanies = (updated: ContractedCompany[]) => {
    setCompanies(updated);
    saveContractedCompanies(updated);
  };

  useEffect(() => {
    const syncFromStorage = () => {
      const stored = getStoredContractedCompanies();
      setCompanies(stored);
    };
    syncFromStorage();
    window.addEventListener('storage', syncFromStorage);
    window.addEventListener('focus', syncFromStorage);
    return () => {
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener('focus', syncFromStorage);
    };
  }, []);

  // Available unique months from tickets & company payments
  const availableMonths = useMemo(() => {
    const set = new Set<string>();
    set.add(currentMonthKey);
    tickets.forEach(t => {
      if (t.createdAt) set.add(t.createdAt.substring(0, 7));
    });
    companies.forEach(c => {
      if (c.payments) {
        c.payments.forEach(p => {
          if (p.periodMonth) set.add(p.periodMonth);
          if (p.date) set.add(p.date.substring(0, 7));
        });
      }
    });
    return Array.from(set).sort().reverse();
  }, [tickets, companies, currentMonthKey]);

  // Filtered companies
  const filteredCompanies = useMemo(() => {
    return companies.filter(c => {
      if (c.isDeleted) return false;
      if (statusFilter === 'active' && !c.isActive) return false;
      if (statusFilter === 'passive' && c.isActive) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const mName = (c.name || '').toLowerCase().includes(q);
        const mCode = (c.code || '').toLowerCase().includes(q);
        const mAuth = (c.authorizedPerson || '').toLowerCase().includes(q);
        const mPhone = (c.phone || '').replace(/\D/g, '').includes(q.replace(/\D/g, ''));
        if (!mName && !mCode && !mAuth && !mPhone) return false;
      }
      return true;
    });
  }, [companies, statusFilter, searchQuery]);

  // Overall Global Contract Metrics
  const globalMetrics = useMemo(() => {
    let totalMonthlyVolume = 0;
    let totalCollected = 0;
    let totalServicesListValue = 0;
    let totalPartsCost = 0;
    let totalSubServiceCost = 0;
    let totalTicketCount = 0;

    companies.filter(comp => !comp.isDeleted).forEach(comp => {
      if (comp.isActive) {
        totalMonthlyVolume += comp.monthlyFee || 0;
      }
      const m = calculateContractCompanyMetrics(
        comp,
        tickets,
        selectedPeriod === 'all' ? undefined : selectedPeriod
      );
      totalCollected += m.totalContractFeePaid;
      totalServicesListValue += m.totalServicesListPrice;
      totalPartsCost += m.totalPartsCost;
      totalSubServiceCost += m.totalSubServiceCost;
      totalTicketCount += m.ticketsCount;
    });

    const totalDirectCost = totalPartsCost + totalSubServiceCost;
    const effectiveRevenue = totalCollected > 0 ? totalCollected : totalMonthlyVolume;
    const netProfit = effectiveRevenue - totalDirectCost;
    const remainingBalance = Math.max(0, effectiveRevenue - totalServicesListValue);
    const overallMargin = effectiveRevenue > 0 ? Math.round((netProfit / effectiveRevenue) * 100) : 0;

    return {
      activeCompaniesCount: companies.filter(c => !c.isDeleted && c.isActive).length,
      totalMonthlyVolume,
      totalCollected,
      totalServicesListValue,
      totalDirectCost,
      totalPartsCost,
      totalSubServiceCost,
      netProfit,
      remainingBalance,
      overallMargin,
      totalTicketCount,
    };
  }, [companies, tickets, selectedPeriod]);

  // Selected company detail data
  const selectedCompany = useMemo(() => {
    if (!selectedCompanyId) return null;
    return companies.find(c => c.id === selectedCompanyId) || null;
  }, [companies, selectedCompanyId]);

  const selectedCompanyMetrics = useMemo(() => {
    if (!selectedCompany) return null;
    return calculateContractCompanyMetrics(
      selectedCompany,
      tickets,
      selectedPeriod === 'all' ? undefined : selectedPeriod
    );
  }, [selectedCompany, tickets, selectedPeriod]);

  // Open Create / Edit Modal
  const handleOpenCreateModal = () => {
    setEditingCompany(null);
    setFormName('');
    setFormCode(`SOZ-${String(companies.length + 1).padStart(3, '0')}`);
    setFormAuthPerson('');
    setFormPhone('');
    setFormEmail('');
    setFormAddress('');
    setFormTaxOffice('');
    setFormTaxNumber('');
    setFormMonthlyFee(50000);
    setFormBillingCycle('Aylık');
    setFormStartDate(new Date().toISOString().substring(0, 10));
    setFormNotes('Aylık sabit sözleşme bedeli dahilinde bakım & onarım hizmetleri verilir.');
    setFormIsActive(true);
    setIsCompanyModalOpen(true);
  };

  const handleOpenEditModal = (comp: ContractedCompany, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditingCompany(comp);
    setFormName(comp.name);
    setFormCode(comp.code || '');
    setFormAuthPerson(comp.authorizedPerson || '');
    setFormPhone(comp.phone);
    setFormEmail(comp.email || '');
    setFormAddress(comp.address || '');
    setFormTaxOffice(comp.taxOffice || '');
    setFormTaxNumber(comp.taxNumber || '');
    setFormMonthlyFee(comp.monthlyFee || 0);
    setFormBillingCycle(comp.billingCycle || 'Aylık');
    setFormStartDate(comp.startDate ? comp.startDate.substring(0, 10) : new Date().toISOString().substring(0, 10));
    setFormNotes(comp.notes || '');
    setFormIsActive(comp.isActive);
    setIsCompanyModalOpen(true);
  };

  const handleSaveCompany = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      alert('Lütfen firma adını giriniz.');
      return;
    }
    if (!formPhone.trim()) {
      alert('Lütfen firma telefon numarasını giriniz.');
      return;
    }

    const nowISO = new Date().toISOString();

    if (editingCompany) {
      // Update existing
      const updated = companies.map(c => {
        if (c.id === editingCompany.id) {
          return {
            ...c,
            name: formName.trim(),
            code: formCode.trim() || undefined,
            authorizedPerson: formAuthPerson.trim() || undefined,
            phone: formPhone.trim(),
            email: formEmail.trim() || undefined,
            address: formAddress.trim() || undefined,
            taxOffice: formTaxOffice.trim() || undefined,
            taxNumber: formTaxNumber.trim() || undefined,
            monthlyFee: Math.max(0, Number(formMonthlyFee) || 0),
            billingCycle: formBillingCycle,
            startDate: new Date(formStartDate).toISOString(),
            notes: formNotes.trim() || undefined,
            isActive: formIsActive,
            updatedAt: nowISO,
          };
        }
        return c;
      });
      handleUpdateCompanies(updated);
      showToast(`✅ "${formName.trim()}" sözleşmeli firma bilgileri başarıyla güncellendi.`);
    } else {
      // Create new
      const newId = generateNextContractCompanyId(companies);
      const newCompany: ContractedCompany = {
        id: newId,
        code: formCode.trim() || `SOZ-${String(companies.length + 1).padStart(3, '0')}`,
        name: formName.trim(),
        authorizedPerson: formAuthPerson.trim() || undefined,
        phone: formPhone.trim(),
        email: formEmail.trim() || undefined,
        address: formAddress.trim() || undefined,
        taxOffice: formTaxOffice.trim() || undefined,
        taxNumber: formTaxNumber.trim() || undefined,
        monthlyFee: Math.max(0, Number(formMonthlyFee) || 0),
        billingCycle: formBillingCycle,
        startDate: new Date(formStartDate).toISOString(),
        notes: formNotes.trim() || undefined,
        isActive: formIsActive,
        payments: [
          {
            id: 'cp_' + Date.now(),
            amount: Math.max(0, Number(formMonthlyFee) || 0),
            date: nowISO,
            periodMonth: selectedPeriod === 'all' ? currentMonthKey : selectedPeriod,
            paymentMethod: 'EFT/Havale',
            notes: 'İlk sözleşme avans girişi',
            recordedBy: 'Sistem',
          },
        ],
        createdAt: nowISO,
        updatedAt: nowISO,
      };
      const updated = [newCompany, ...companies];
      handleUpdateCompanies(updated);
      setSelectedCompanyId(newId);
      showToast(`🎉 "${formName.trim()}" sözleşmeli firması başarıyla eklendi.`);
    }

    setIsCompanyModalOpen(false);
  };

  const handleDeleteCompany = (comp: ContractedCompany, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!isAdmin) {
      alert('Yetkisiz İşlem: Sözleşmeli firma silme yetkisi sadece yönetici (admin) hesaplarında bulunmaktadır.');
      return;
    }
    if (confirm(`"${comp.name}" sözleşmeli firması gizliye alınsın mı? Firma ve geçmiş servis/tahsilat kayıtları veritabanında korunacaktır.`)) {
      const nowISO = new Date().toISOString();
      const updated = companies.map(c => c.id === comp.id ? {
        ...c, isDeleted: true, isActive: false, deletedAt: nowISO,
        deletedByUser: currentUser?.username || 'admin',
        deletedByName: currentUser?.name || currentUser?.username || 'Yönetici', updatedAt: nowISO
      } : c);
      handleUpdateCompanies(updated);
      if (selectedCompanyId === comp.id) setSelectedCompanyId(null);
      showToast(`🗃️ "${comp.name}" gizliye alındı; geçmiş kayıtları korundu.`);
    }
  };

  // Open Payment Collection Modal
  const handleOpenPaymentModal = (comp: ContractedCompany, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setPaymentCompany(comp);
    setPaymentAmount(comp.monthlyFee || 50000);
    setPaymentMethod('EFT/Havale');
    setPaymentDate(new Date().toISOString().substring(0, 10));
    setPaymentPeriodMonth(selectedPeriod === 'all' ? currentMonthKey : selectedPeriod);
    setPaymentNotes(`${comp.name} periyodik sözleşme bedeli tahsilatı`);
    setIsPaymentModalOpen(true);
  };

  const handleSavePayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentCompany) return;
    const amt = Math.max(0, Number(paymentAmount) || 0);
    if (amt <= 0) {
      alert('Lütfen geçerli bir tahsilat tutarı giriniz.');
      return;
    }

    const nowISO = new Date(paymentDate).toISOString();
    const newRecord: ContractPaymentRecord = {
      id: 'cp_' + Date.now() + '_' + Math.random().toString(36).substring(2, 5),
      amount: amt,
      date: nowISO,
      periodMonth: paymentPeriodMonth,
      paymentMethod,
      notes: paymentNotes.trim() || undefined,
      recordedBy: 'Sistem Yöneticisi',
    };

    const updated = companies.map(c => {
      if (c.id === paymentCompany.id) {
        const existingPayments = Array.isArray(c.payments) ? c.payments : [];
        return {
          ...c,
          payments: [newRecord, ...existingPayments],
          updatedAt: new Date().toISOString(),
        };
      }
      return c;
    });

    handleUpdateCompanies(updated);
    setIsPaymentModalOpen(false);
    showToast(`💰 ${paymentCompany.name} için ${formatTL(amt)} sözleşme tahsilatı kaydedildi ve ana bakiye güncellendi.`);
  };

  // Print Statement for selected company
  const handlePrintStatement = () => {
    if (!selectedCompany || !selectedCompanyMetrics) return;
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toastMessage && (
        <div className="bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-2.5 text-xs font-bold">
            <CheckCircle2 className="w-5 h-5 text-emerald-200 shrink-0" />
            <span>{toastMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setToastMessage(null)}
            className="p-1 hover:bg-emerald-700 rounded-lg text-emerald-100 cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Top Banner & Action Header */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 text-white p-6 rounded-2xl border border-slate-700 shadow-md">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-purple-600/30 border border-purple-400/40 text-purple-300 flex items-center justify-center font-bold shadow-inner">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold tracking-tight text-white">
                  Sözleşmeli Kurumsal Firmalar
                </h2>
              </div>
              <p className="text-xs text-slate-300 mt-1 max-w-3xl leading-relaxed">
                Anlaşmalı kurumsal firmalara ait aylık/dönemsel sözleşme bedellerini tanımlayın, servislerde girilen parça & işçilik liste fiyatlarını bu bakiyeden otomatik mahsup edin ve ay sonunda sözleşmenin yeterliliğini, harcanan gerçek parça maliyetini ve net kâr/zararı anlık takip edin.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0 w-full lg:w-auto">
            {/* Period Selector */}
            <div className="flex items-center gap-1.5 bg-slate-800/90 border border-slate-700 px-3 py-2 rounded-xl text-xs">
              <Calendar className="w-4 h-4 text-purple-400" />
              <select
                value={selectedPeriod}
                onChange={e => setSelectedPeriod(e.target.value)}
                className="bg-transparent text-white font-bold focus:outline-none cursor-pointer"
              >
                <option value="all" className="bg-slate-900 text-white">Tüm Dönemler</option>
                {availableMonths.map(m => (
                  <option key={m} value={m} className="bg-slate-900 text-white">
                    {m === currentMonthKey ? `Bu Ay (${m})` : m}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={handleOpenCreateModal}
              className="bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs px-4 py-2.5 rounded-xl flex items-center gap-1.5 shadow-md shadow-purple-600/30 transition-all hover:scale-[1.02] cursor-pointer shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span>Yeni Sözleşmeli Firma</span>
            </button>
          </div>
        </div>
      </div>

      {/* Global Contract KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 1. Toplam Sözleşme Geliri / Hacmi */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
            <span>Sözleşme Sabit Hacmi / Gelir</span>
            <span className="p-1 rounded-lg bg-purple-50 text-purple-700">
              <Wallet className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-2xl font-mono font-extrabold text-purple-700">
              {formatTL(globalMetrics.totalCollected > 0 ? globalMetrics.totalCollected : globalMetrics.totalMonthlyVolume)}
            </span>
            <span className="text-[11px] font-bold text-slate-500">
              {globalMetrics.activeCompaniesCount} Aktif Firma
            </span>
          </div>
          <div className="mt-1 text-[11px] text-slate-400 flex items-center gap-1">
            <span>Ana kasaya işleyen garanti sözleşme tutarı</span>
          </div>
        </div>

        {/* 2. Mahsup Edilen Servisler Toplamı (Liste Fiyatı) */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
            <span>Kullanılan Servis Liste Tutarı</span>
            <span className="p-1 rounded-lg bg-blue-50 text-blue-700">
              <Layers className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-2xl font-mono font-extrabold text-blue-700">
              {formatTL(globalMetrics.totalServicesListValue)}
            </span>
            <span className="text-[11px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
              {globalMetrics.totalTicketCount} Servis
            </span>
          </div>
          <div className="mt-1 text-[11px] text-slate-400">
            Bakiyeden düşülen form tutarları (Ana kasaya mükerrer yazılmaz)
          </div>
        </div>

        {/* 3. Kalan Sözleşme Bakiyesi */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
            <span>Kalan Sözleşme Bakiyesi</span>
            <span className="p-1 rounded-lg bg-emerald-50 text-emerald-700">
              <ShieldCheck className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-2xl font-mono font-extrabold text-emerald-600">
              {formatTL(globalMetrics.remainingBalance)}
            </span>
            <span className="text-[11px] font-bold text-slate-500">
              {selectedPeriod === 'all' ? 'Genel Kalan' : 'Dönem Sonu Kalan'}
            </span>
          </div>
          <div className="mt-1 text-[11px] text-emerald-700 flex items-center gap-1 font-medium">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            <span>Müşterilerin kullanabileceği açık limit</span>
          </div>
        </div>

        {/* 4. Gerçek Kâr & Net Maliyet Marjı */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
            <span>Sözleşme Net Kârı</span>
            <span className={`p-1 rounded-lg ${globalMetrics.netProfit >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              <TrendingUp className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className={`text-2xl font-mono font-extrabold ${globalMetrics.netProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
              {formatTL(globalMetrics.netProfit)}
            </span>
            <span className="text-[11px] font-extrabold px-2 py-0.5 rounded-full bg-slate-100 text-slate-800">
              %{globalMetrics.overallMargin} Marj
            </span>
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            Gerçek Harcanan Parça & Alt Servis: {formatTL(globalMetrics.totalDirectCost)}
          </div>
        </div>
      </div>

      {/* Main Content: Split List and Company Details */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Companies Directory List (5 cols) */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-bold text-xs text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-purple-600" />
                <span>Kayıtlı Sözleşmeli Firmalar ({filteredCompanies.length})</span>
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setStatusFilter('all')}
                  className={`text-[11px] font-bold px-2 py-1 rounded-lg transition-colors cursor-pointer ${statusFilter === 'all' ? 'bg-purple-100 text-purple-800' : 'text-slate-500 hover:bg-slate-100'}`}
                >
                  Tümü
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('active')}
                  className={`text-[11px] font-bold px-2 py-1 rounded-lg transition-colors cursor-pointer ${statusFilter === 'active' ? 'bg-emerald-100 text-emerald-800' : 'text-slate-500 hover:bg-slate-100'}`}
                >
                  Aktif
                </button>
              </div>
            </div>

            {/* Search Box */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Firma adı, kodu veya yetkili ara..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-8.5 pr-3 py-1.5 text-xs border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 bg-slate-50/60"
              />
            </div>
          </div>

          {/* Companies Cards List */}
          <div className="space-y-3 max-h-[680px] overflow-y-auto pr-1">
            {filteredCompanies.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl border border-dashed border-slate-300 text-center space-y-2">
                <Building2 className="w-8 h-8 text-slate-300 mx-auto" />
                <p className="text-xs font-bold text-slate-700">Kayıtlı Sözleşmeli Firma Bulunamadı</p>
                <p className="text-[11px] text-slate-400">Yeni bir firma sözleşmesi tanımlayarak başlayabilirsiniz.</p>
                <button
                  type="button"
                  onClick={handleOpenCreateModal}
                  className="mt-2 text-xs font-bold text-purple-600 hover:text-purple-800 underline cursor-pointer"
                >
                  + Yeni Firma Tanımla
                </button>
              </div>
            ) : (
              filteredCompanies.map(comp => {
                const metrics = calculateContractCompanyMetrics(
                  comp,
                  tickets,
                  selectedPeriod === 'all' ? undefined : selectedPeriod
                );
                const isSelected = selectedCompanyId === comp.id;

                return (
                  <div
                    key={comp.id}
                    onClick={() => setSelectedCompanyId(comp.id)}
                    className={`bg-white p-4 rounded-2xl border transition-all cursor-pointer group relative overflow-hidden ${
                      isSelected
                        ? 'border-purple-600 ring-2 ring-purple-600/20 shadow-md'
                        : 'border-slate-200 hover:border-purple-300 hover:shadow-xs'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2.5">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs shrink-0 ${
                          isSelected ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-700'
                        }`}>
                          <Building2 className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-xs text-slate-900 group-hover:text-purple-700">
                              {comp.name}
                            </span>
                            {comp.code && (
                              <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.2 rounded">
                                {comp.code}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                            {comp.authorizedPerson && <span>Yetkili: {comp.authorizedPerson}</span>}
                            <span>• {comp.phone}</span>
                          </div>
                        </div>
                      </div>

                      {/* Health / Sufficiency Badge */}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 border ${
                        metrics.healthStatus === 'healthy'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : metrics.healthStatus === 'warning'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-rose-50 text-rose-700 border-rose-200'
                      }`}>
                        {metrics.healthStatus === 'healthy'
                          ? '✅ Kârlı / Yeterli'
                          : metrics.healthStatus === 'warning'
                          ? '⚠️ %80+ Kullanıldı'
                          : '❌ Bakiye Aşıldı'}
                      </span>
                    </div>

                    {/* Progress Bar of Budget Utilization */}
                    <div className="mt-3 space-y-1">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-500 font-medium">Sözleşme / Kalan Bakiye</span>
                        <span className="font-mono font-bold text-slate-800">
                          {formatTL(metrics.remainingBalance)} / {formatTL(metrics.effectiveContractRevenue)}
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden flex">
                        <div
                          className={`h-full transition-all ${
                            metrics.healthStatus === 'healthy'
                              ? 'bg-purple-600'
                              : metrics.healthStatus === 'warning'
                              ? 'bg-amber-500'
                              : 'bg-rose-500'
                          }`}
                          style={{ width: `${Math.min(100, metrics.utilizationPercent)}%` }}
                        />
                      </div>
                    </div>

                    {/* Quick Footer Stats */}
                    <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-[11px]">
                      <span className="text-slate-500">
                        {metrics.ticketsCount} Servis Kaydı
                      </span>
                      <span className="font-bold font-mono text-emerald-700">
                        Net Kâr: {formatTL(metrics.netProfit)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column: Selected Company Detailed Statement & Analysis (7 cols) */}
        <div className="lg:col-span-7">
          {selectedCompany && selectedCompanyMetrics ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden space-y-6 p-6">
              {/* Company Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-5 border-slate-100">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold bg-purple-100 text-purple-800 px-2 py-0.5 rounded-md">
                      {selectedCompany.code || selectedCompany.id}
                    </span>
                    <h3 className="text-lg font-bold text-slate-900">
                      {selectedCompany.name}
                    </h3>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      selectedCompany.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {selectedCompany.isActive ? 'Aktif Anlaşma' : 'Pasif'}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-1">
                    {selectedCompany.authorizedPerson && (
                      <span>Yetkili: <strong>{selectedCompany.authorizedPerson}</strong></span>
                    )}
                    <span>Tel: <strong>{selectedCompany.phone}</strong></span>
                    {selectedCompany.email && <span>E-posta: {selectedCompany.email}</span>}
                    {selectedCompany.taxNumber && (
                      <span>VN: {selectedCompany.taxOffice} / {selectedCompany.taxNumber}</span>
                    )}
                  </div>
                </div>

                {/* Header Action Buttons */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleOpenPaymentModal(selectedCompany)}
                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                    title="Bu firma için yeni sözleşme tahsilatı gir"
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    <span>Tahsilat Ekle</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleOpenEditModal(selectedCompany)}
                    className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors cursor-pointer"
                    title="Firma Bilgilerini Düzenle"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>

                  {isAdmin && (
                    <button
                      type="button"
                      onClick={e => handleDeleteCompany(selectedCompany, e)}
                      className="p-2 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-xl transition-colors cursor-pointer"
                      title="Firmayı Gizliye Al (Sadece Yönetici)"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Statement Summary Analysis Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-purple-50/70 border border-purple-200/80 p-3.5 rounded-xl">
                  <span className="text-[11px] font-bold text-purple-900 block">Sözleşme Bakiye Limiti</span>
                  <span className="text-xl font-mono font-extrabold text-purple-700 block mt-1">
                    {formatTL(selectedCompanyMetrics.effectiveContractRevenue)}
                  </span>
                  <span className="text-[10px] text-purple-600 block mt-0.5">
                    {selectedCompanyMetrics.totalContractFeePaid > 0 ? 'Yapılan tahsilat toplamı' : 'Tanımlı aylık sabit limit'}
                  </span>
                </div>

                <div className="bg-blue-50/70 border border-blue-200/80 p-3.5 rounded-xl">
                  <span className="text-[11px] font-bold text-blue-900 block">Mahsup Edilen Servis Fiyatı</span>
                  <span className="text-xl font-mono font-extrabold text-blue-700 block mt-1">
                    {formatTL(selectedCompanyMetrics.totalServicesListPrice)}
                  </span>
                  <span className="text-[10px] text-blue-600 block mt-0.5">
                    {selectedCompanyMetrics.ticketsCount} Adet servis formu toplamı
                  </span>
                </div>

                <div className={`p-3.5 rounded-xl border ${
                  selectedCompanyMetrics.isBalanceExceeded
                    ? 'bg-rose-50 border-rose-200 text-rose-900'
                    : 'bg-emerald-50/70 border-emerald-200/80 text-emerald-900'
                }`}>
                  <span className="text-[11px] font-bold block">
                    {selectedCompanyMetrics.isBalanceExceeded ? 'Aşılan Limit Tutarı' : 'Kalan Kullanılabilir Bakiye'}
                  </span>
                  <span className={`text-xl font-mono font-extrabold block mt-1 ${
                    selectedCompanyMetrics.isBalanceExceeded ? 'text-rose-700' : 'text-emerald-700'
                  }`}>
                    {selectedCompanyMetrics.isBalanceExceeded
                      ? `-${formatTL(selectedCompanyMetrics.exceededAmount)}`
                      : formatTL(selectedCompanyMetrics.remainingBalance)}
                  </span>
                  <span className="text-[10px] block mt-0.5 opacity-80">
                    {selectedCompanyMetrics.isBalanceExceeded ? 'Sözleşme yenileme veya ek fatura gerektirir' : 'Limit dahilinde onarıma devam edilebilir'}
                  </span>
                </div>
              </div>

              {/* Cost & Sufficiency Evaluation Block */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs text-slate-800 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    <span>Dönem Sonu Sözleşme Yeterlilik & Kâr/Zarar Analizi</span>
                  </span>
                  <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${
                    selectedCompanyMetrics.healthStatus === 'healthy'
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                      : selectedCompanyMetrics.healthStatus === 'warning'
                      ? 'bg-amber-100 text-amber-800 border-amber-300'
                      : 'bg-rose-100 text-rose-800 border-rose-300'
                  }`}>
                    {selectedCompanyMetrics.healthStatus === 'healthy'
                      ? 'Sözleşme Kârlı ve Yeterli'
                      : selectedCompanyMetrics.healthStatus === 'warning'
                      ? 'Kritik Seviye (%80+ Kullanıldı)'
                      : 'Zararda / Bakiye Aşımı Var'}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="p-2.5 bg-white rounded-lg border border-slate-200">
                    <span className="text-[10px] text-slate-400 block font-semibold">Parça Maliyeti</span>
                    <span className="font-mono font-bold text-slate-800">{formatTL(selectedCompanyMetrics.totalPartsCost)}</span>
                  </div>
                  <div className="p-2.5 bg-white rounded-lg border border-slate-200">
                    <span className="text-[10px] text-slate-400 block font-semibold">Alt Servis Maliyeti</span>
                    <span className="font-mono font-bold text-slate-800">{formatTL(selectedCompanyMetrics.totalSubServiceCost)}</span>
                  </div>
                  <div className="p-2.5 bg-white rounded-lg border border-slate-200">
                    <span className="text-[10px] text-slate-400 block font-semibold">Toplam Net Maliyet</span>
                    <span className="font-mono font-bold text-rose-600">{formatTL(selectedCompanyMetrics.totalDirectCost)}</span>
                  </div>
                  <div className="p-2.5 bg-white rounded-lg border border-slate-200">
                    <span className="text-[10px] text-slate-400 block font-semibold">Net Kâr / Marj</span>
                    <span className="font-mono font-bold text-emerald-700">
                      {formatTL(selectedCompanyMetrics.netProfit)} (%{Math.round(selectedCompanyMetrics.profitMargin)})
                    </span>
                  </div>
                </div>
              </div>

              {/* Tickets Table For This Company */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs text-slate-800 flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span>Sözleşme Kapsamında Açılan Servis Kayıtları ({selectedCompanyMetrics.ticketsCount})</span>
                  </span>
                </div>

                {selectedCompanyMetrics.companyTickets.length === 0 ? (
                  <div className="p-6 text-center text-slate-400 text-xs border border-dashed rounded-xl bg-slate-50">
                    Bu sözleşmeli firma için seçili dönemde henüz bir servis kaydı açılmamış.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-600">
                          <th className="py-2.5 px-3">Servis Kodu & Tarih</th>
                          <th className="py-2.5 px-3">Cihaz & Arıza</th>
                          <th className="py-2.5 px-3">Durum</th>
                          <th className="py-2.5 px-3 text-right">Form Liste Fiyatı</th>
                          <th className="py-2.5 px-3 text-right">Parça Maliyeti</th>
                          <th className="py-2.5 px-3 text-center">İşlem</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedCompanyMetrics.companyTickets.map(t => {
                          const { grandTotal } = calculateTicketTotal(
                            t.parts || [],
                            t.laborCost || 0,
                            t.vatRate ?? 20,
                            t.discount || 0,
                            t.isVatIncluded || false
                          );
                          const pCost = calculateTicketPartsCost(t.parts || []);
                          const statusInfo = getStatusColors(t.status);

                          return (
                            <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                              <td className="py-2.5 px-3">
                                <span className="font-mono font-bold text-blue-700 block">{t.id}</span>
                                <span className="text-[10px] text-slate-400">{formatDateShortTR(t.createdAt)}</span>
                              </td>
                              <td className="py-2.5 px-3">
                                <span className="font-semibold text-slate-800 block">
                                  {t.deviceBrand} {t.deviceModel}
                                </span>
                                <span className="text-[10px] text-slate-500 truncate max-w-xs block">
                                  {t.issueDescription}
                                </span>
                              </td>
                              <td className="py-2.5 px-3">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusInfo.bg} ${statusInfo.text} ${statusInfo.border}`}>
                                  {STATUS_LABELS[t.status]}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-right">
                                <span className="font-mono font-bold text-slate-900 block">{formatTL(grandTotal)}</span>
                                <span className="text-[9px] text-purple-700 font-semibold bg-purple-50 px-1 py-0.2 rounded">
                                  Mahsup Edildi
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-right font-mono text-slate-600">
                                {formatTL(pCost)}
                              </td>
                              <td className="py-2.5 px-3 text-center">
                                {onSelectTicket && (
                                  <button
                                    type="button"
                                    onClick={() => onSelectTicket(t)}
                                    className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                                    title="Servis Detayını Aç"
                                  >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Payment Records Table */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs text-slate-800 flex items-center gap-1.5">
                    <CreditCard className="w-4 h-4 text-emerald-600" />
                    <span>Sözleşme Tahsilat Geçmişi ({selectedCompany.payments?.length || 0})</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => handleOpenPaymentModal(selectedCompany)}
                    className="text-[11px] font-bold text-emerald-700 hover:text-emerald-800 hover:underline cursor-pointer"
                  >
                    + Tahsilat Girişi
                  </button>
                </div>

                {(!selectedCompany.payments || selectedCompany.payments.length === 0) ? (
                  <div className="p-4 text-center text-slate-400 text-xs border border-dashed rounded-xl bg-slate-50">
                    Henüz tahsilat kaydı bulunmuyor.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-600">
                          <th className="py-2 px-3">Tarih</th>
                          <th className="py-2 px-3">Dönem</th>
                          <th className="py-2 px-3">Yöntem</th>
                          <th className="py-2 px-3">Açıklama</th>
                          <th className="py-2 px-3 text-right">Tahsil Edilen Tutar</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedCompany.payments.map(p => (
                          <tr key={p.id} className="hover:bg-slate-50">
                            <td className="py-2 px-3 text-slate-600">{formatDateShortTR(p.date)}</td>
                            <td className="py-2 px-3 font-semibold text-purple-800">{p.periodMonth || '-'}</td>
                            <td className="py-2 px-3 font-medium text-slate-700">{p.paymentMethod}</td>
                            <td className="py-2 px-3 text-slate-500">{p.notes || '-'}</td>
                            <td className="py-2 px-3 text-right font-mono font-bold text-emerald-700">
                              {formatTL(p.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center mx-auto">
                <Building2 className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-slate-800">Sözleşmeli Firma Seçiniz</h3>
              <p className="text-xs text-slate-400 max-w-sm mx-auto">
                Sol taraftaki listeden bir sözleşmeli firma seçerek cari hesap ekstresini, limit mahsuplarını, harcanan parça maliyetlerini ve net kâr/zarar analizini görüntüleyebilirsiniz.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* MODAL 1: CREATE / EDIT CONTRACTED COMPANY */}
      {/* ========================================================================= */}
      {isCompanyModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-slate-200 overflow-hidden text-left animate-in fade-in zoom-in-95">
            <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between border-b border-slate-800">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center text-white font-bold">
                  <Building2 className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white leading-snug">
                    {editingCompany ? 'Sözleşmeli Firma Düzenle' : 'Yeni Sözleşmeli Kurumsal Firma Tanımla'}
                  </h3>
                  <p className="text-xs text-slate-400">
                    Aylık sözleşme tutarı ve cari hesap bilgilerini girin
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsCompanyModalOpen(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveCompany} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Firma Adı & Ünvanı <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Örn: ABC Kurumsal Bilişim Ltd. Şti."
                    value={formName}
                    onChange={e => setFormName(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Firma / Sözleşme Kodu
                  </label>
                  <input
                    type="text"
                    placeholder="Örn: SOZ-001"
                    value={formCode}
                    onChange={e => setFormCode(e.target.value)}
                    className="w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Yetkili Kişi Adı Soyadı
                  </label>
                  <input
                    type="text"
                    placeholder="Örn: Ahmet Yılmaz"
                    value={formAuthPerson}
                    onChange={e => setFormAuthPerson(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Telefon Numarası <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Örn: 0555 123 4567"
                    value={formPhone}
                    onChange={e => setFormPhone(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    E-Posta Adresi
                  </label>
                  <input
                    type="email"
                    placeholder="Örn: info@firma.com"
                    value={formEmail}
                    onChange={e => setFormEmail(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Firma Adresi
                  </label>
                  <input
                    type="text"
                    placeholder="İl, İlçe, Mahalle, Cadde / Sokak"
                    value={formAddress}
                    onChange={e => setFormAddress(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Vergi Dairesi
                  </label>
                  <input
                    type="text"
                    placeholder="Örn: Maslak VD"
                    value={formTaxOffice}
                    onChange={e => setFormTaxOffice(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Vergi Numarası / T.C.
                  </label>
                  <input
                    type="text"
                    placeholder="Örn: 1234567890"
                    value={formTaxNumber}
                    onChange={e => setFormTaxNumber(e.target.value)}
                    className="w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>

                {/* Monthly Fee */}
                <div className="sm:col-span-2 bg-purple-50/80 p-4 rounded-xl border border-purple-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-purple-900 mb-1">
                        Aylık / Dönemsel Sözleşme Bedeli (TL) <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <DollarSign className="w-4 h-4 text-purple-600 absolute left-3 top-2.5" />
                        <input
                          type="number"
                          required
                          min="0"
                          step="100"
                          value={formMonthlyFee}
                          onChange={e => setFormMonthlyFee(parseFloat(e.target.value) || 0)}
                          className="w-full pl-9 pr-3 py-2 text-sm font-mono font-bold text-purple-950 border border-purple-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                        />
                      </div>
                      <p className="text-[10px] text-purple-700 mt-1">
                        Örn: 50.000 TL bakiye tanımlanır. Servis kayıtlarında girilen tutarlar bu bakiyeden düşer.
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-purple-900 mb-1">
                        Faturalandırma & Anlaşma Periyodu
                      </label>
                      <select
                        value={formBillingCycle}
                        onChange={e => setFormBillingCycle(e.target.value as any)}
                        className="w-full px-3 py-2 text-xs font-semibold text-slate-800 border border-purple-300 rounded-lg bg-white focus:outline-none"
                      >
                        <option value="Aylık">Aylık Sözleşme</option>
                        <option value="3 Aylık">3 Aylık (Çeyreklik)</option>
                        <option value="6 Aylık">6 Aylık</option>
                        <option value="Yıllık">Yıllık Sözleşme</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Sözleşme Başlangıç Tarihi
                  </label>
                  <input
                    type="date"
                    value={formStartDate}
                    onChange={e => setFormStartDate(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none"
                  />
                </div>

                <div className="flex items-center mt-5">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-800">
                    <input
                      type="checkbox"
                      checked={formIsActive}
                      onChange={e => setFormIsActive(e.target.checked)}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span>Sözleşme Aktif</span>
                  </label>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Özel Anlaşma Notları ve Şartlar
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Bakım periyotları, özel şartlar, parça iskonto oranları vb."
                    value={formNotes}
                    onChange={e => setFormNotes(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsCompanyModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl shadow-md transition-colors cursor-pointer"
                >
                  {editingCompany ? 'Değişiklikleri Kaydet' : 'Sözleşmeli Firmayı Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: RECORD CONTRACT PAYMENT (SÖZLEŞME TAHSİLATI GİRİŞİ) */}
      {/* ========================================================================= */}
      {isPaymentModalOpen && paymentCompany && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 overflow-hidden text-left animate-in fade-in zoom-in-95">
            <div className="bg-emerald-900 text-white px-6 py-4 flex items-center justify-between border-b border-emerald-800">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center text-white font-bold">
                  <DollarSign className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white leading-snug">
                    Sözleşme Tahsilatı Kaydet
                  </h3>
                  <p className="text-xs text-emerald-200">
                    {paymentCompany.name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsPaymentModalOpen(false)}
                className="text-emerald-300 hover:text-white p-1 rounded-lg hover:bg-emerald-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSavePayment} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  Tahsil Edilen Tutar (TL) <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="w-4 h-4 text-emerald-600 absolute left-3 top-2.5" />
                  <input
                    type="number"
                    required
                    min="1"
                    step="100"
                    value={paymentAmount}
                    onChange={e => setPaymentAmount(parseFloat(e.target.value) || 0)}
                    className="w-full pl-9 pr-3 py-2 text-base font-mono font-extrabold text-emerald-950 border border-emerald-300 rounded-xl bg-emerald-50/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Tahsilat Tarihi
                  </label>
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={e => setPaymentDate(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Sözleşme Dönemi
                  </label>
                  <input
                    type="month"
                    value={paymentPeriodMonth}
                    onChange={e => setPaymentPeriodMonth(e.target.value)}
                    className="w-full px-3 py-2 text-xs font-mono font-bold border border-slate-300 rounded-lg focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Ödeme Yöntemi
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['EFT/Havale', 'Nakit', 'Kredi Karti'] as PaymentMethod[]).map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setPaymentMethod(m)}
                      className={`py-2 px-2 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                        paymentMethod === m
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {m === 'Kredi Karti' ? 'Kredi Kartı' : m}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Açıklama / Dekont Notu
                </label>
                <input
                  type="text"
                  placeholder="Örn: Garanti Bankası EFT ile tahsil edildi"
                  value={paymentNotes}
                  onChange={e => setPaymentNotes(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none"
                />
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-[11px] text-slate-600">
                💡 <strong>Not:</strong> Bu tutar firmanın ön ödemeli sözleşme bakiyesine eklenir ve ana kasa raporunda <strong>Sözleşme Geliri</strong> olarak kayıt altına alınır.
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsPaymentModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-md transition-colors cursor-pointer"
                >
                  Tahsilatı Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
